import { t } from '../../../lib/i18n';
import type { FinancePeriod } from '../types';
import { FinancePeriodTabs } from './finance-tabs';

interface FinanceHeaderProps {
  /** Pre-composed summary line — every number already resolved to a literal. */
  eyebrow: string;
  period: FinancePeriod;
  /** The instant the period strip names its months from; `computedAt` when known. */
  periodNow?: Date;
  onPeriodChange: (period: FinancePeriod) => void;
  onIssueInvoice: () => void;
  /**
   * Why the invoice action is unavailable, or null when it is available.
   *
   * A REASON rather than a boolean, because the two preconditions fail for
   * unrelated causes and want unrelated responses: no tariff in force means the
   * pricing document is unreadable and nothing can be quoted (an incident), and
   * no clients means the platform is empty (a Tuesday). Disabling the button
   * with one shrug for both is how the first gets mistaken for the second.
   */
  issueBlockedReason: string | null;
}

/**
 * Title, summary line and the screen's two actions — `Redesign-D-Admin`,
 * «Фінанси».
 *
 * The actions and the period strip are desktop-only, and that is the artboard's
 * decision rather than a shortcut: the mobile artboard's finance header carries
 * the title and eyebrow alone, and folds the period strip into the revenue
 * card. Issuing an invoice is desk work.
 *
 * «Експорт .xlsx» is rendered as the artboard draws it but carries
 * `aria-disabled` and no handler: there is no export endpoint. It is NOT the
 * HTML `disabled` attribute, which would repaint the label in the user agent's
 * grey and make the button a different colour from the design for a reason that
 * has nothing to do with the design.
 */
export function FinanceHeader({
  eyebrow,
  period,
  periodNow,
  onPeriodChange,
  onIssueInvoice,
  issueBlockedReason,
}: FinanceHeaderProps) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 leading-[normal]">
      <div className="min-w-0">
        <h1 className="text-[26px] leading-[1.1] font-semibold tracking-[-0.02em] lg:text-[30px]">
          {t('adminFinance.title')}
        </h1>
        <p
          data-testid="finance-eyebrow"
          className="mt-1.5 font-mono text-[10px] tracking-[0.12em] text-cf-ink-subtle uppercase lg:mt-2 lg:text-[11px] lg:tracking-[0.14em]"
        >
          {eyebrow}
        </p>
      </div>

      <div className="hidden flex-wrap items-center gap-2 lg:flex">
        <FinancePeriodTabs active={period} now={periodNow} onChange={onPeriodChange} />

        <button
          type="button"
          aria-disabled
          title={t('adminFinance.exportDisabled')}
          className="h-10 cursor-not-allowed rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-[14px] font-semibold"
        >
          {t('adminFinance.export')}
        </button>

        <button
          type="button"
          onClick={onIssueInvoice}
          disabled={issueBlockedReason !== null}
          title={issueBlockedReason ?? undefined}
          className="h-10 rounded-cf-md bg-cf-brand px-4 text-[14px] font-semibold text-white hover:bg-cf-brand-hover disabled:opacity-40 disabled:hover:bg-cf-brand"
        >
          {t('adminFinance.issueInvoice')}
        </button>
      </div>
    </div>
  );
}
